import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getType } from "../../actions/studensts";
import UNAUTHORIZED from "../UNAUTHORIZED";
import StudentList from "../StudentList";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { exportExcelState, exportExcelDeliveryHold, getAramexSheet } from "../../actions/studensts";
import AramexSheetModal from "./AramexSheetModal";

export default function Deliver() {
  const dispatch = useDispatch();
  const location = useLocation();
  const studentType = useLocation().pathname.split("/")[3];
  const { students } = useSelector((state) => state.students);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showAramexSheet, setShowAramexSheet] = useState(false);
  const count = useSelector((state) => state.students.count);
  const user = JSON.parse(localStorage.getItem("profile")).value[0].role;

  useEffect(() => {
    dispatch({ type: "CLEAR" });
    if (!hasSearched) return;
    const state = convertType(studentType);
    const timeOutId = setTimeout(
      () => dispatch(getType("deliver", state, search)),
      500
    );
    return () => clearTimeout(timeOutId);
  }, [location, search, dispatch, studentType, hasSearched]);

  useEffect(() => {
    setHasSearched(false);
  }, [location]);

  useEffect(() => {
    if (students) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [students]);

  const handleExport = async () => {
    try {
      const { data } = await exportExcelState("deliver_onsite");
      const students = data.data;

      // Same Top Achiever criteria as the ACT Top Achievers Report: any
      // ACT1 or ACT2 subject score between 30 and 36. FindAllWithState
      // already returns act1_score1/2/3 and act2_score1/2/3 for every
      // student, so this can be computed here without a backend change.
      const isTopAchiever = (s) => {
        const scores = [
          s.act1_score1, s.act1_score2, s.act1_score3,
          s.act2_score1, s.act2_score2, s.act2_score3,
        ];
        return scores.some((v) => v !== null && v !== undefined && Number(v) >= 30 && Number(v) <= 36);
      };
      const topAchieversCount = students.filter(isTopAchiever).length;
      const nonTopAchieversCount = students.length - topAchieversCount;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "AATC System";
      workbook.created = new Date();
      const sheet = workbook.addWorksheet("Delivery Onsite Report");

      // --- Title Row ---
      sheet.mergeCells("A1:E1");
      const titleCell = sheet.getCell("A1");
      titleCell.value = "Delivery Onsite Students Report";
      titleCell.font = { name: "Arial", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(1).height = 35;

      // --- Total Count Row ---
      sheet.mergeCells("A2:E2");
      const countCell = sheet.getCell("A2");
      countCell.value = `Total Students: ${students.length}`;
      countCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF1F4E79" } };
      countCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
      countCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(2).height = 28;

      // --- Top Achievers Count Row (yellow, matches the row highlight below) ---
      sheet.mergeCells("A3:E3");
      const topCell = sheet.getCell("A3");
      topCell.value = `Top Achievers Count: ${topAchieversCount}`;
      topCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF7A5D00" } };
      topCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFEB9C" } };
      topCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(3).height = 28;

      // --- Non-Top Achievers Count Row ---
      sheet.mergeCells("A4:E4");
      const nonTopCell = sheet.getCell("A4");
      nonTopCell.value = `Non-Top Achievers Count: ${nonTopAchieversCount}`;
      nonTopCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF1F4E79" } };
      nonTopCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
      nonTopCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(4).height = 28;

      // --- Empty spacer row ---
      sheet.getRow(5).height = 8;

      // --- Header Row (row 6) ---
      const headers = [
        "National ID",
        "Passport Number",
        "Full Name",
        "Phone Number",
        "Invoice Date",
        "Reason",
        "Comment",
      ];
      const headerRow = sheet.getRow(6);
      headerRow.values = headers;
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FF1F4E79" } },
          bottom: { style: "thin", color: { argb: "FF1F4E79" } },
          left: { style: "thin", color: { argb: "FF1F4E79" } },
          right: { style: "thin", color: { argb: "FF1F4E79" } },
        };
      });

      // --- Data Rows ---
      students.forEach((s, idx) => {
        const row = sheet.getRow(7 + idx);
        row.values = [
          s["id"],
          s["passport_number"],
          s["full_name"],
          s["phone"],
          s["invoice_date"],
          s["delivery_hold_reason"],
          s["delivery_hold"],
        ];
        const topAchiever = isTopAchiever(s);
        const isEven = idx % 2 === 0;
        // Top Achievers get one uniform yellow fill (not zebra-striped) +
        // bold text, same idea as the red restricted-school highlight used
        // in the Schools Report.
        const bg = topAchiever ? "FFFFEB9C" : (isEven ? "FFF2F7FB" : "FFFFFFFF");
        row.eachCell((cell) => {
          cell.font = topAchiever
            ? { name: "Arial", size: 10, bold: true, color: { argb: "FF7A5D00" } }
            : { name: "Arial", size: 10 };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          cell.border = {
            top: { style: "thin", color: { argb: "FFB4C6E7" } },
            bottom: { style: "thin", color: { argb: "FFB4C6E7" } },
            left: { style: "thin", color: { argb: "FFB4C6E7" } },
            right: { style: "thin", color: { argb: "FFB4C6E7" } },
          };
        });
      });

      // --- Column Widths ---
      sheet.columns = [
        { width: 20 },  // National ID
        { width: 20 },  // Passport Number
        { width: 30 },  // Full Name
        { width: 18 },  // Phone Number
        { width: 22 },  // Invoice Date
        { width: 30 },  // Reason
        { width: 30 },  // Comment
      ];

      // --- Generate & Download ---
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "deliver_onsite_report.xlsx");
    } catch (error) {
      console.error("Export to Excel failed:", error);
    }
  };

  // Aramex shipping sheet: NAME comes from arabic_data.name_in_arabic,
  // ADDRESS/CITY are typed in through the Edit Sheet modal, and the two phone
  // columns are split from the students phone field by the backend.
  const handleExportAramex = async () => {
    try {
      const res = await getAramexSheet();
      if (res?.status !== 200) return;
      const rows = res.data.data;

      const confirmedCount = rows.filter((s) => s.confirmed).length;
      const nonConfirmedCount = rows.length - confirmedCount;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "AATC System";
      workbook.created = new Date();
      const sheet = workbook.addWorksheet("Aramex Shipping Sheet");

      // --- Title Row ---
      sheet.mergeCells("A1:E1");
      const titleCell = sheet.getCell("A1");
      titleCell.value = "Aramex Shipping Sheet";
      titleCell.font = { name: "Arial", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(1).height = 35;

      // --- Total Count Row ---
      sheet.mergeCells("A2:E2");
      const countCell = sheet.getCell("A2");
      countCell.value = `Total Students: ${rows.length}    |    Confirmed: ${confirmedCount}    |    Non-Confirmed: ${nonConfirmedCount}`;
      countCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF1F4E79" } };
      countCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
      countCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(2).height = 28;

      // --- Empty spacer row ---
      sheet.getRow(3).height = 8;

      // --- Header Row (row 4) ---
      const headers = ["NAME", "ADDRESS", "CITY", "PHONE NUMBER", "PHONE NUMBER2"];
      const headerRow = sheet.getRow(4);
      headerRow.values = headers;
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FF1F4E79" } },
          bottom: { style: "thin", color: { argb: "FF1F4E79" } },
          left: { style: "thin", color: { argb: "FF1F4E79" } },
          right: { style: "thin", color: { argb: "FF1F4E79" } },
        };
      });

      // --- Data Rows ---
      rows.forEach((s, idx) => {
        const row = sheet.getRow(5 + idx);
        row.values = [
          s["name"],
          s["address"],
          s["city"],
          s["phone_number"],
          s["phone_number2"],
        ];
        const bg = idx % 2 === 0 ? "FFF2F7FB" : "FFFFFFFF";
        row.eachCell((cell) => {
          cell.font = { name: "Arial", size: 10 };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          cell.border = {
            top: { style: "thin", color: { argb: "FFB4C6E7" } },
            bottom: { style: "thin", color: { argb: "FFB4C6E7" } },
            left: { style: "thin", color: { argb: "FFB4C6E7" } },
            right: { style: "thin", color: { argb: "FFB4C6E7" } },
          };
        });
      });

      // --- Column Widths ---
      sheet.columns = [
        { width: 32 },  // NAME
        { width: 45 },  // ADDRESS
        { width: 20 },  // CITY
        { width: 20 },  // PHONE NUMBER
        { width: 20 },  // PHONE NUMBER2
      ];

      // --- Generate & Download ---
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "aramex_shipping_sheet.xlsx");
    } catch (error) {
      console.error("Export to Excel failed:", error);
    }
  };

  const handleExportDeliveryHold = async () => {
    try {
      const { data } = await exportExcelDeliveryHold();
      const students = data.data;

      // Same Top Achiever criteria as the ACT Top Achievers Report: any
      // ACT2 subject score between 30 and 36.
      const isTopAchiever = (s) => Number(s["Top Achiever"]) === 1;
      const topAchieversCount = students.filter(isTopAchiever).length;
      const nonTopAchieversCount = students.length - topAchieversCount;

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "AATC System";
      workbook.created = new Date();
      const sheet = workbook.addWorksheet("Delivery Hold Report");

      // --- Title Row ---
      sheet.mergeCells("A1:G1");
      const titleCell = sheet.getCell("A1");
      titleCell.value = "Delivery Hold Students Report";
      titleCell.font = { name: "Arial", size: 16, bold: true, color: { argb: "FFFFFFFF" } };
      titleCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(1).height = 35;

      // --- Total Count Row ---
      sheet.mergeCells("A2:G2");
      const countCell = sheet.getCell("A2");
      countCell.value = `Total Students: ${students.length}`;
      countCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF1F4E79" } };
      countCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
      countCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(2).height = 28;

      // --- Top Achievers Count Row (yellow, matches the row highlight below) ---
      sheet.mergeCells("A3:G3");
      const topCell = sheet.getCell("A3");
      topCell.value = `Top Achievers Count: ${topAchieversCount}`;
      topCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF7A5D00" } };
      topCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFEB9C" } };
      topCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(3).height = 28;

      // --- Non-Top Achievers Count Row ---
      sheet.mergeCells("A4:G4");
      const nonTopCell = sheet.getCell("A4");
      nonTopCell.value = `Non-Top Achievers Count: ${nonTopAchieversCount}`;
      nonTopCell.font = { name: "Arial", size: 13, bold: true, color: { argb: "FF1F4E79" } };
      nonTopCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
      nonTopCell.alignment = { horizontal: "center", vertical: "middle" };
      sheet.getRow(4).height = 28;

      // --- Empty spacer row ---
      sheet.getRow(5).height = 8;

      // --- Header Row (row 6) ---
      const headers = [
        "National ID",
        "Passport Number",
        "Full Name",
        "Phone Number",
        "Reason",
        "Comment",
        "Invoice Date",
      ];
      const headerRow = sheet.getRow(6);
      headerRow.values = headers;
      headerRow.height = 26;
      headerRow.eachCell((cell) => {
        cell.font = { name: "Arial", size: 11, bold: true, color: { argb: "FFFFFFFF" } };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF2E75B6" } };
        cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FF1F4E79" } },
          bottom: { style: "thin", color: { argb: "FF1F4E79" } },
          left: { style: "thin", color: { argb: "FF1F4E79" } },
          right: { style: "thin", color: { argb: "FF1F4E79" } },
        };
      });

      // --- Data Rows ---
      students.forEach((s, idx) => {
        const row = sheet.getRow(7 + idx);
        row.values = [
          s["National ID"],
          s["Passport Number"],
          s["Full Name"],
          s["Phone Number"],
          s["Reason"],
          s["Comment"],
          s["Invoice Date"],
        ];
        const topAchiever = isTopAchiever(s);
        const isEven = idx % 2 === 0;
        // Top Achievers get one uniform yellow fill (not zebra-striped) +
        // bold text, same idea as the red restricted-school highlight used
        // in the Schools Report.
        const bg = topAchiever ? "FFFFEB9C" : (isEven ? "FFF2F7FB" : "FFFFFFFF");
        row.eachCell((cell) => {
          cell.font = topAchiever
            ? { name: "Arial", size: 10, bold: true, color: { argb: "FF7A5D00" } }
            : { name: "Arial", size: 10 };
          cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
          cell.border = {
            top: { style: "thin", color: { argb: "FFB4C6E7" } },
            bottom: { style: "thin", color: { argb: "FFB4C6E7" } },
            left: { style: "thin", color: { argb: "FFB4C6E7" } },
            right: { style: "thin", color: { argb: "FFB4C6E7" } },
          };
        });
      });

      // --- Column Widths ---
      sheet.columns = [
        { width: 20 },  // National ID
        { width: 20 },  // Passport Number
        { width: 30 },  // Full Name
        { width: 18 },  // Phone Number
        { width: 28 },  // Reason
        { width: 35 },  // Comment
        { width: 22 },  // Invoice Date
      ];

      // --- Generate & Download ---
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "delivery_hold_report.xlsx");
    } catch (error) {
      console.error("Export to Excel failed:", error);
    }
  };

  return (
    <div>
      {user !== "user" && user !== "admin" ? (
        <UNAUTHORIZED />
      ) : (
        <div>
          <div className="flex items-center justify-between px-6 pt-4">
            <h1 className="text-2xl pt-0 pb-5 text-white">
              {studentType === "deliver_onsite"
                ? "Delivery Onsite"
                : studentType === "aramex"
                ? "Aramex"
                : studentType === "delivery_hold"
                ? "Delivery Hold"
                : ""}
            </h1>
            {studentType === "deliver_onsite" && (
              <button
                onClick={handleExport}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
              >
                Export to Excel
              </button>
            )}
            {studentType === "aramex" && (
              <div className="flex gap-3">
                <button
                  onClick={() => setShowAramexSheet(true)}
                  className="bg-amber-600 hover:bg-amber-700 text-white font-semibold py-2 px-4 rounded"
                >
                  Edit Sheet
                </button>
                <button
                  onClick={handleExportAramex}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
                >
                  Export to Excel
                </button>
              </div>
            )}
            {studentType === "delivery_hold" && (
              <button
                onClick={handleExportDeliveryHold}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded"
              >
                Export to Excel
              </button>
            )}
          </div>

          {showAramexSheet && (
            <AramexSheetModal onClose={() => setShowAramexSheet(false)} />
          )}
          <div className="flex justify-center">
            <div
              className="lg:w-3/5 md:w-3/4 w-full min-h-screen border shadow
              flex flex-col bg-white p-6 rounded-lg"
            >
              <StudentList
                setSearch={(val) => {
                  setSearch(val);
                  setHasSearched(true);
                }}
                students={students}
                search={search}
                loading={loading}
                count={count}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function convertType(studentType) {
  switch (studentType) {
    case "deliver_onsite":
      return "deliver_onsite";
    case "aramex":
      return "aramex";
    case "delivery_hold":
      return "delivery_hold";
    default:
      return "deliver_onsite";
  }
}
